import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export const useFetchCallback = (mainQuery, backupQuery, options = {}) => {
  const [useBackup, setUseBackup] = useState(false);
  const user = useSelector(state => state.auth.user);

  const { strictFallback = false, ...apolloOptions } = options;

  const userHasCompletedOnboarding = user?.['custom:checkid-sign-doc'] === 'signature_success';

  const skipQuery = !user;

  const {
    data: mainData,
    loading: mainLoading,
    error: mainError,
    refetch: refetchMain,
    ...mainQueryProps
  } = useQuery(mainQuery, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: skipQuery,
    ...apolloOptions,
  });

  const hasGraphQLErrors = mainQueryProps?.error?.graphQLErrors?.length > 0;
  const hasNullOrEmptyData =
    !mainData ||
    Object.values(mainData).some(
      value => value === null || (Array.isArray(value) && value.length === 0),
    );

  useEffect(() => {
    const shouldFallback = strictFallback
      ? !mainLoading && userHasCompletedOnboarding && hasGraphQLErrors
      : !mainLoading && userHasCompletedOnboarding && (hasGraphQLErrors || hasNullOrEmptyData);

    if (shouldFallback) {
      setUseBackup(true);
    }
  }, [
    mainLoading,
    hasGraphQLErrors,
    hasNullOrEmptyData,
    userHasCompletedOnboarding,
    strictFallback,
  ]);

  const {
    data: backupData,
    loading: backupLoading,
    error: backupError,
    refetch: refetchBackup,
    ...backupQueryProps
  } = useQuery(backupQuery, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !useBackup,
    ...apolloOptions,
  });

  const data = useBackup ? backupData : mainData;
  const loading = useBackup ? backupLoading : mainLoading;
  const error = useBackup ? backupError : mainError;

  return {
    data,
    loading,
    error,
    refetch: useBackup ? refetchBackup : refetchMain,
    mainQueryProps,
    backupQueryProps,
  };
};
